<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Áreas</h1>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-tooltip text="Descargar">
            <wz-button variant="outline" @click="downloadReport()">
              <i class="ri-download-2-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por descripción"
            @search="onSearch"
            @input="search = $event"
          />
          <wz-button @click="$router.push({ name: 'area-create' })">Agregar</wz-button>
        </template>

        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'description' }" @click="toggleSort('description')">
              Descripción <i :class="['wz-th-sort-icon', sortIcon('description')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'status' }" @click="toggleSort('status')">
              Estado <i :class="['wz-th-sort-icon', sortIcon('status')]"></i>
            </th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in sortedList(list)" :key="o.id">
            <td>{{ o.description }}</td>
            <td>
              <wz-badge :variant="statusVariant(o.status)" pill>
                {{ getStatus(o.status).label }}
              </wz-badge>
            </td>
            <td style="text-align: center;">
              <wz-dropdown
                :items="[
                  { key: 'edit',   label: 'Editar' },
                  { key: 'status', label: getStatus(o.status).action },
                ]"
                placement="left"
                @click="handleAction(o, $event)"
              />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <wz-pagination
          v-if="list.length"
          :total="total"
          :current-page="page"
          :per-page="pageSize"
          @change="onPaginationChange"
        />
        <div class="table-no-results" v-else>
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </wz-table-footer>

    </wz-table-container>

    <wz-confirm-modal
      v-model="confirmModal.show"
      :title="confirmModal.title"
      :description="confirmModal.description"
      :accept-text="confirmModal.acceptText"
      :cancel-text="confirmModal.cancelText"
      @confirm="onModalConfirm"
      @cancel="closeConfirmModal"
    />
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { Util } from "@wizepe/wizeui"
import { FilterMixin } from "@wizepe/wizeui"
import { PaginationMixin } from "@wizepe/wizeui"
import { SortMixin, ConfirmModalMixin } from '@wizepe/wizeui'
import { Constants as LocalConstants } from '@/core/utils'
import { AreaService } from '@/core/services'
import fileDownload from 'js-file-download'

export default {
  mixins: [SortMixin, PaginationMixin, FilterMixin, ConfirmModalMixin],
  data() {
    return {
      list: [],
      filterOptions: [
        {
          label: 'Estado',
          key: 'status',
          type: 'SINGLE',
          options: [LocalConstants.AreaStatus.ACTIVE, LocalConstants.AreaStatus.INACTIVE],
        },
      ],
    }
  },
  methods: {
    statusVariant(status) {
      return this.getStatus(status)?.color || 'dark'
    },
    handleAction(o, item) {
      if (item.key === 'edit')   this.$router.push({ name: 'area-edit', params: { id: o.id } })
      if (item.key === 'status') this.openChangeStatus(o)
    },
    onPaginationChange({ page, perPage }) {
      this.page = page
      this.pageSize = perPage
      this.loadData()
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const params = { page: this.page - 1, size: this.pageSize, sort: 'id' }
        const data = { search: this.search, ...this.filters }
        const response = await AreaService.list(data, params)
        this.list = response.payload.content.map(item => ({ ...item }))
        this.total = response.payload.totalElements
        this.pages = response.payload.totalPages
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar las áreas')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    getStatus(key) {
      return Util.searchValue(parseInt(key), LocalConstants.AreaStatus)
    },
    openChangeStatus(o) {
      this.selectedOption = o
      this.openConfirmModal({
        title:       'Cambiar estado',
        description: '¿Está seguro que desea cambiar el estado de esta área?',
        acceptText:  'Aceptar',
        action:      'update',
      })
    },
    onModalConfirm() {
      this.closeConfirmModal()
      if (this.confirmModal.action === 'update') this.changeStatus()
    },
    async changeStatus() {
      try {
        await this.$store.dispatch('app/loading', true)
        let status = this.selectedOption.status
        status = status == LocalConstants.AreaStatus.ACTIVE.key
          ? LocalConstants.AreaStatus.INACTIVE.key
          : LocalConstants.AreaStatus.ACTIVE.key
        await AreaService.changeStatus(this.selectedOption.id, status)
        await this.loadData()
        Alert.success('Estado actualizado correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al cambiar el estado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async downloadReport() {
      try {
        await this.$store.dispatch('app/loading', true)
        const data = { search: this.search, ...this.filters }
        const response = await AreaService.download(data)
        fileDownload(response.data, Util.getFileName(response))
      } catch (error) {
        console.error(error)
        Alert.error('Error al descargar el reporte')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    await this.loadData(this.page, this.pageSize)
  },
}
</script>

<style lang="stylus">
</style>
